import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { toast } from 'react-hot-toast';
import {
    FaTimes,
    FaRegCircle,
    FaCheckCircle,
    FaPlus
} from 'react-icons/fa/index.js';
import callOysterApi from '../../utils/call-oyster-api.js';
import callOracleApi from '../../utils/call-oracle-api.js';

const TaskDetailsModal = ({
    task,
    subtasks,
    onClose,
    onSubtasksChanged,
    onTaskStatusUpdated,
    currentUserEmail,
    currentUserTeam,
    isItLeadership,
    hasManagementRights
}) => {
    const [newSubtaskTitle, setNewSubtaskTitle] = useState('');
    const [isCreatingSubtask, setIsCreatingSubtask] = useState(false);
    const [isConfirmingMainComplete, setIsConfirmingMainComplete] =
        useState(false);
    const [isCompleting, setIsCompleting] = useState(false);
    const [localSubtasks, setLocalSubtasks] = useState([]);
    const confirmTimeoutRef = useRef(null);
    const [missionRequest, setMissionRequest] = useState(null);

    useEffect(() => {
        if (!task || task.item_type !== 'rock') return;
        const taskId =
            task.task_id ||
            (task.id?.includes('_')
                ? task.id.split('_').slice(1).join('_')
                : task.id);
        if (!taskId) return;
        callOracleApi(
            `/mission-requests/by-task/${encodeURIComponent(taskId)}`,
            'GET'
        )
            .then((r) => r.json())
            .then((d) => {
                if (d.success) setMissionRequest(d.payload);
            })
            .catch(() => {});
    }, [task?.id]);

    if (!task) return null;

    const isTaskOverdue = task.status === 'needsAction' && task.sla?.is_overdue;
    const canComplete =
        task.status !== 'completed' &&
        (isItLeadership ||
            task.assignee_email === currentUserEmail ||
            (hasManagementRights && task.team_id === currentUserTeam));

    // Merge prop subtasks with optimistic local ones (de-dup by id)
    const mergedSubtasks = React.useMemo(() => {
        const propIds = new Set((subtasks || []).map((s) => s.id));
        const extras = localSubtasks.filter((s) => !propIds.has(s.id));
        return [...(subtasks || []), ...extras];
    }, [subtasks, localSubtasks]);

    // Clear local optimistic subtasks once props catch up
    React.useEffect(() => {
        if (localSubtasks.length > 0) {
            const propIds = new Set((subtasks || []).map((s) => s.id));
            const remaining = localSubtasks.filter((s) => !propIds.has(s.id));
            if (remaining.length !== localSubtasks.length) {
                setLocalSubtasks(remaining);
            }
        }
    }, [subtasks]);

    const markTaskCompleteApi = async (taskToComplete) => {
        setIsCompleting(true);

        // Optimistically update subtask status BEFORE API call
        const isSubtask = taskToComplete.id !== task.id;
        let prevLocalSubtasks;
        if (isSubtask) {
            prevLocalSubtasks = [...localSubtasks];
            setLocalSubtasks((prev) => {
                const existing = prev.find((s) => s.id === taskToComplete.id);
                if (existing) {
                    return prev.map((s) =>
                        s.id === taskToComplete.id
                            ? { ...s, status: 'completed' }
                            : s
                    );
                }
                return [...prev, { ...taskToComplete, status: 'completed' }];
            });
        }

        try {
            const response = await callOysterApi(
                `/users/${encodeURIComponent(
                    taskToComplete.assignee_email
                )}/todos/${encodeURIComponent(taskToComplete.id)}/status`,
                'PATCH',
                { status: 'completed' }
            );
            if (response.ok) {
                toast.success('Marked as complete!');
                if (onTaskStatusUpdated) {
                    onTaskStatusUpdated(taskToComplete.id, 'completed');
                }

                if (onSubtasksChanged) {
                    onSubtasksChanged();
                }
                // If it's the main task being completed, close the modal
                if (taskToComplete.id === task.id) {
                    onClose();
                }
            } else {
                toast.error('Failed to update status');
                // Roll back optimistic update
                if (isSubtask) {
                    setLocalSubtasks(prevLocalSubtasks);
                }
            }
        } catch (error) {
            console.error('Error completing task:', error);
            toast.error('Network error');
            // Roll back optimistic update
            if (isSubtask) {
                setLocalSubtasks(prevLocalSubtasks);
            }
        } finally {
            setIsCompleting(false);
            setIsConfirmingMainComplete(false);
        }
    };

    const submitSubtask = async () => {
        if (newSubtaskTitle.trim() === '') return;
        setIsCreatingSubtask(true);
        try {
            // If task_id is somehow missing or null, fallback to stripping the source prefix from the composite ID
            let actualParentId = task.task_id;
            if (!actualParentId && task.id && task.id.includes('_')) {
                actualParentId = task.id.split('_').slice(1).join('_');
            }

            const payload = {
                title: newSubtaskTitle.trim(),
                assignee_email: task.assignee_email,
                item_type: 'task',
                parent_task_id: actualParentId, // Pass computed parent to API
                tasklist_id: task.tasklist_id || 'default' // Pass tasklist for scoping
            };
            console.log('[DEBUG_SUBTASK_PAYLOAD]', payload, 'task:', task);

            const response = await callOysterApi(
                `/teams/${encodeURIComponent(task.team_id || 'default')}/todos`,
                'POST',
                payload
            );

            if (response.ok) {
                const data = await response.json();
                toast.success('Subtask created');
                setNewSubtaskTitle('');

                // Optimistically add the new subtask to local state
                const newSub = data.payload || {
                    id: `temp_${Date.now()}`,
                    title: newSubtaskTitle.trim(),
                    status: 'needsAction',
                    assignee_email: task.assignee_email,
                    parent_task_id: task.task_id
                };
                setLocalSubtasks((prev) => [...prev, newSub]);

                // Still trigger background refresh for data consistency
                if (onSubtasksChanged) {
                    onSubtasksChanged();
                }
            } else {
                const data = await response.json();
                toast.error(data.message || 'Failed to create subtask');
            }
        } catch (error) {
            console.error('Error creating subtask:', error);
            toast.error('Network error while creating subtask');
        } finally {
            setIsCreatingSubtask(false);
        }
    };

    const handleCreateSubtask = async (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            await submitSubtask();
        }
    };

    return ReactDOM.createPortal(
        <div className="task-details-overlay" onClick={onClose}>
            <div
                className="task-details-modal"
                onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="task-modal-header">
                    <div className="task-modal-header-left">
                        <span className={`type-badge badge-${task.item_type}`}>
                            {task.item_type === 'rock'
                                ? '🎯 Mission'
                                : '✅ Task'}
                        </span>
                        <h2 className="task-modal-title">{task.title}</h2>
                    </div>
                    <button className="btn-close-modal" onClick={onClose}>
                        <FaTimes />
                    </button>
                </div>

                {/* Body Split */}
                <div className="task-modal-body">
                    {/* Main Content (Left) */}
                    <div className="task-modal-main">
                        <div className="task-section">
                            <h3 className="section-label">Description</h3>
                            <div className="task-description">
                                {task.notes ? (
                                    <p>{task.notes}</p>
                                ) : (
                                    <p className="no-description">
                                        No description provided.
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="task-section mt-4">
                            <h3 className="section-label">Subtasks</h3>
                            <div className="subtasks-list-container">
                                {mergedSubtasks && mergedSubtasks.length > 0 ? (
                                    <ul className="subtasks-list">
                                        {mergedSubtasks.map((sub) => (
                                            <li
                                                key={sub.id}
                                                className={`subtask-item ${sub.status}`}>
                                                <button
                                                    className="btn-mark-subtask"
                                                    onClick={() =>
                                                        canComplete &&
                                                        !isCompleting &&
                                                        markTaskCompleteApi(sub)
                                                    }
                                                    disabled={
                                                        !canComplete ||
                                                        isCompleting ||
                                                        sub.status ===
                                                            'completed'
                                                    }>
                                                    {sub.status ===
                                                    'completed' ? (
                                                        <FaCheckCircle className="icon-completed" />
                                                    ) : (
                                                        <FaRegCircle className="icon-pending" />
                                                    )}
                                                </button>
                                                <span className="subtask-title">
                                                    {sub.title}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="no-subtasks">
                                        No subtasks yet.
                                    </p>
                                )}

                                <div className="add-subtask-row">
                                    <FaPlus className="add-subtask-icon" />
                                    <input
                                        type="text"
                                        className="add-subtask-input"
                                        placeholder={
                                            isCreatingSubtask
                                                ? 'Creating...'
                                                : 'Add a subtask...'
                                        }
                                        value={newSubtaskTitle}
                                        onChange={(e) =>
                                            setNewSubtaskTitle(e.target.value)
                                        }
                                        onKeyDown={handleCreateSubtask}
                                        disabled={isCreatingSubtask}
                                    />
                                    {newSubtaskTitle.trim() !== '' && (
                                        <button
                                            className="btn-add-subtask"
                                            onClick={submitSubtask}
                                            disabled={isCreatingSubtask}>
                                            {isCreatingSubtask
                                                ? '...'
                                                : 'Create'}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Mission submission details (only for approved missions) */}
                    {task.item_type === 'rock' && missionRequest && (
                        <div className="task-section mt-4">
                            <h3 className="section-label">Mission Details</h3>
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '0.5rem',
                                    fontSize: '0.875rem'
                                }}>
                                {missionRequest.executiveObjective && (
                                    <MissionDetailRow
                                        label="Executive Objective"
                                        value={
                                            missionRequest.executiveObjective
                                        }
                                    />
                                )}
                                {missionRequest.problemStatement && (
                                    <MissionDetailRow
                                        label="Problem to Solve"
                                        value={missionRequest.problemStatement}
                                    />
                                )}
                                {missionRequest.successMeasure && (
                                    <MissionDetailRow
                                        label="Success Measure"
                                        value={missionRequest.successMeasure}
                                    />
                                )}
                                {missionRequest.estimatedRoi && (
                                    <MissionDetailRow
                                        label="Estimated ROI"
                                        value={missionRequest.estimatedRoi}
                                    />
                                )}
                                {missionRequest.estimatedWeeks && (
                                    <MissionDetailRow
                                        label="Est. Weeks"
                                        value={missionRequest.estimatedWeeks}
                                    />
                                )}
                                {missionRequest.qProfitPoints != null && (
                                    <MissionDetailRow
                                        label="Q Profit Points"
                                        value={missionRequest.qProfitPoints}
                                        highlight
                                    />
                                )}
                                {missionRequest.involvedDepartments?.length >
                                    0 && (
                                    <MissionDetailRow
                                        label="Departments"
                                        value={missionRequest.involvedDepartments.join(
                                            ', '
                                        )}
                                    />
                                )}
                                {missionRequest.leadershipNotes && (
                                    <MissionDetailRow
                                        label="Leadership Notes"
                                        value={missionRequest.leadershipNotes}
                                    />
                                )}
                            </div>
                        </div>
                    )}

                    {/* Sidebar / Meta Info (Right) */}
                    <div className="task-modal-sidebar">
                        <div className="sidebar-group">
                            <label>Status</label>
                            <div className="sidebar-value">
                                {(() => {
                                    if (task.status === 'completed') {
                                        return (
                                            <span className="status-badge status-completed">
                                                Completed
                                            </span>
                                        );
                                    }
                                    if (isTaskOverdue) {
                                        return (
                                            <span className="status-badge status-overdue">
                                                Overdue
                                            </span>
                                        );
                                    }
                                    return (
                                        <span className="status-badge status-pending">
                                            Pending
                                        </span>
                                    );
                                })()}
                            </div>
                        </div>

                        <div className="sidebar-group">
                            <label>Assignee</label>
                            <div className="sidebar-value">
                                {task.assignee_first_name}{' '}
                                {task.assignee_last_name}
                                <div className="sidebar-subvalue">
                                    {task.assignee_email}
                                </div>
                            </div>
                        </div>

                        <div className="sidebar-group">
                            <label>Team</label>
                            <div className="sidebar-value">
                                <span className="team-pill">
                                    {task.team_id}
                                </span>
                            </div>
                        </div>

                        <div className="sidebar-group">
                            <label>Due Date</label>
                            <div className="sidebar-value">
                                {task.due_date?.value || task.due_date || '-'}
                            </div>
                        </div>

                        <div className="sidebar-group">
                            <label>SLA Target</label>
                            <div className="sidebar-value">
                                {task.sla?.target_hours
                                    ? `${task.sla.target_hours} Hours`
                                    : '-'}
                            </div>
                        </div>

                        <div className="sidebar-group">
                            <label>Source</label>
                            <div className="sidebar-value">
                                {(() => {
                                    const source = task.source || 'oracle';
                                    const sourceMap = {
                                        oracle: {
                                            label: 'Oracle',
                                            className: 'source-oracle'
                                        },
                                        googletasks: {
                                            label: 'Google Tasks',
                                            className: 'source-google'
                                        },
                                        level_10: {
                                            label: 'Level 10',
                                            className: 'source-level10'
                                        }
                                    };
                                    const info = sourceMap[source] || {
                                        label: source,
                                        className: ''
                                    };
                                    return (
                                        <span
                                            className={`source-badge ${info.className}`}>
                                            {info.label}
                                        </span>
                                    );
                                })()}
                            </div>
                        </div>

                        {canComplete && (
                            <div className="sidebar-actions mt-4">
                                <button
                                    className={`btn-primary-action w-100 ${
                                        isConfirmingMainComplete
                                            ? 'btn-delete'
                                            : ''
                                    }`}
                                    onClick={() => {
                                        if (isConfirmingMainComplete) {
                                            if (confirmTimeoutRef.current)
                                                clearTimeout(
                                                    confirmTimeoutRef.current
                                                );
                                            markTaskCompleteApi(task);
                                        } else {
                                            setIsConfirmingMainComplete(true);
                                        }
                                    }}
                                    onMouseEnter={() => {
                                        if (confirmTimeoutRef.current) {
                                            clearTimeout(
                                                confirmTimeoutRef.current
                                            );
                                        }
                                    }}
                                    onMouseLeave={() => {
                                        if (
                                            isConfirmingMainComplete &&
                                            !isCompleting
                                        ) {
                                            confirmTimeoutRef.current =
                                                setTimeout(() => {
                                                    setIsConfirmingMainComplete(
                                                        false
                                                    );
                                                }, 3000);
                                        }
                                    }}
                                    disabled={isCompleting}>
                                    {(() => {
                                        if (isCompleting)
                                            return 'Completing...';
                                        if (isConfirmingMainComplete)
                                            return 'Click to Confirm';
                                        return '✓ Mark Complete';
                                    })()}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

const MissionDetailRow = ({ label, value, highlight }) => (
    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
        <span
            style={{
                fontWeight: 600,
                color: '#555',
                minWidth: 140,
                flexShrink: 0
            }}>
            {label}:
        </span>
        <span
            style={{
                color: highlight ? '#27ae60' : undefined,
                fontWeight: highlight ? 700 : undefined
            }}>
            {value}
        </span>
    </div>
);

MissionDetailRow.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    highlight: PropTypes.bool
};

TaskDetailsModal.propTypes = {
    task: PropTypes.object,
    subtasks: PropTypes.array.isRequired,
    onClose: PropTypes.func.isRequired,
    onSubtasksChanged: PropTypes.func.isRequired,
    onTaskStatusUpdated: PropTypes.func,
    currentUserEmail: PropTypes.string.isRequired,
    currentUserTeam: PropTypes.string,
    isItLeadership: PropTypes.bool,
    hasManagementRights: PropTypes.bool.isRequired
};

export default TaskDetailsModal;
